import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "status-timeline-container" }
const _hoisted_2 = { class: "status-header" }
const _hoisted_3 = { class: "timeline-title" }
const _hoisted_4 = {
  key: 0,
  class: "status-loading"
}
const _hoisted_5 = {
  key: 1,
  class: "status-timeline"
}
const _hoisted_6 = { class: "status-icon" }
const _hoisted_7 = { class: "status-info" }
const _hoisted_8 = { class: "status-label" }
const _hoisted_9 = {
  key: 0,
  class: "status-date"
}
const _hoisted_10 = {
  key: 0,
  class: "status-actions"
}
const _hoisted_11 = ["onClick", "title"]

import { ref, computed, h, type VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import PermissionGuard from '@/components/common/PermissionGuard.vue'

// Types
interface TimelineStep {
  status: string
  date: string | null
  isCurrent: boolean
  isCompleted: boolean
}

interface StatusHistoryDto {
  id: number
  fromStatus: string | null
  toStatus: string
  changedByLogin: string
  changedByName: string
  changedAt: string
  formattedChangedAt: string
  event: string
  comment?: string
}

interface StatusIconConfig {
  [key: string]: (step: TimelineStep) => VNode
}

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'StatusTimeline',
  props: {
    title: {},
    currentStatus: {},
    timeline: {},
    possibleStatuses: {},
    loading: { type: Boolean },
    i18nPrefix: {},
    actionI18nPrefix: {},
    transitionToKey: {},
    updatePermission: {},
    projectId: {},
    statusIcons: {}
  },
  emits: ["statusChange", "refresh"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props

// Emits
const emit = __emit

// State
const showModal = ref(false)
const targetStatus = ref<string | null>(null)

// Composables
const { t } = useI18n()

// Computed timeline steps
const timelineSteps = computed<TimelineStep[]>(() => {
  if (!props.timeline || props.timeline.length === 0) {
    // If no timeline, show current status only
    return [{
      status: props.currentStatus,
      date: null,
      isCurrent: true,
      isCompleted: false
    }]
  }
  
  const steps: TimelineStep[] = []
  
  // Add the initial status (fromStatus of the first transition)
  if (props.timeline[0].fromStatus) {
    steps.push({
      status: props.timeline[0].fromStatus,
      date: null, // Initial status doesn't have a date
      isCurrent: false,
      isCompleted: true
    })
  }
  
  // Add all transitions
  props.timeline.forEach((entry: StatusHistoryDto, index: number) => {
    const isLast = index === props.timeline.length - 1
    steps.push({
      status: entry.toStatus,
      date: entry.formattedChangedAt || null,
      isCurrent: isLast && entry.toStatus === props.currentStatus,
      isCompleted: !isLast || entry.toStatus !== props.currentStatus
    })
  })
  
  return steps
})

// Get status label from i18n (for timeline display)
const getStatusLabel = (status: string): string => {
  return t(`${props.i18nPrefix}.${status}`)
}

// Get action button label from i18n (uses verbs)
const getActionLabel = (status: string): string => {
  // Use actionI18nPrefix if provided, otherwise fallback to i18nPrefix
  const prefix = props.actionI18nPrefix || props.i18nPrefix
  
  // Context-aware action labels for specific transitions
  // For issues: OPEN -> IN_PROGRESS should be "START", PAUSED -> IN_PROGRESS should be "RESUME"
  // For phases: NOT_STARTED -> IN_PROGRESS should be "START", ON_HOLD -> IN_PROGRESS should be "RESUME"
  // For tasks: PENDING -> IN_PROGRESS should be "START", ON_HOLD/PAUSED -> IN_PROGRESS should be "RESUME"
  if (status === 'IN_PROGRESS' && props.currentStatus) {
    // Starting work: OPEN/NOT_STARTED/PENDING -> IN_PROGRESS
    if (['OPEN', 'NOT_STARTED', 'PENDING'].includes(props.currentStatus)) {
      // Try to get START translation, fallback to IN_PROGRESS if not found
      const startKey = `${prefix}.START`
      const translation = t(startKey)
      if (translation !== startKey) {
        return translation
      }
    } 
    // Resuming work: PAUSED/ON_HOLD -> IN_PROGRESS
    else if (['PAUSED', 'ON_HOLD'].includes(props.currentStatus)) {
      // Try to get RESUME translation, fallback to IN_PROGRESS if not found
      const resumeKey = `${prefix}.RESUME`
      const translation = t(resumeKey)
      if (translation !== resumeKey) {
        return translation
      }
    }
  }
  
  return t(`${prefix}.${status}`)
}

// Get CSS class for status action button
const getStatusActionClass = (status: string): string => {
  return `status-action-btn--${status.toLowerCase()}`
}

// Handle status change button click
const handleStatusChange = (status: string) => {
  targetStatus.value = status
  showModal.value = true
  emit('statusChange', status)
}

// Handle modal success
const handleModalSuccess = () => {
  showModal.value = false
  emit('refresh')
}

// Close modal
const closeModal = () => {
  showModal.value = false
  targetStatus.value = null
}

// Default status icons
const defaultStatusIcons: StatusIconConfig = {
  OPEN: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('circle', {
      cx: 12,
      cy: 12,
      r: 8,
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2
    })
  ]),
  
  NOT_STARTED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('circle', {
      cx: 12,
      cy: 12,
      r: 8,
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2
    })
  ]),
  
  DRAFT: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('path', {
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2,
      d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8'
    })
  ]),
  
  PLANNED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('path', {
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2,
      d: 'M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1'
    })
  ]),
  
  IN_PROGRESS: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polygon', {
      fill: getIconFill(step),
      points: '5 3 19 12 5 21 5 3'
    })
  ]),
  
  ACTIVE: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polygon', {
      fill: getIconFill(step),
      points: '5 3 19 12 5 21 5 3'
    })
  ]),
  
  ON_HOLD: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 6,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      }),
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 14,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      })
    ])
  ]),
  
  PAUSED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 6,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      }),
      h('rect', {
        fill: getIconFill(step),
        stroke: getIconStroke(step),
        'stroke-width': 0,
        x: 14,
        y: 4,
        width: 4,
        height: 16,
        rx: 1
      })
    ])
  ]),
  
  RESOLVED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polyline', {
      stroke: getIconStroke(step),
      'stroke-width': 2,
      fill: 'none',
      points: '20 6 9 17 4 12'
    })
  ]),
  
  COMPLETED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('polyline', {
      stroke: getIconStroke(step),
      'stroke-width': 2,
      fill: 'none',
      points: '20 6 9 17 4 12'
    })
  ]),
  
  CLOSED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('circle', { 
      cx: 12, 
      cy: 12, 
      r: 10, 
      fill: 'none', 
      stroke: getIconStroke(step), 
      'stroke-width': 2 
    }),
    h('path', { 
      d: 'M12,6V12L16,14', 
      fill: 'none', 
      stroke: getIconStroke(step), 
      'stroke-width': 2 
    })
  ]),
  
  REJECTED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 18,
        y1: 6,
        x2: 6,
        y2: 18
      }),
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 6,
        y1: 6,
        x2: 18,
        y2: 18
      })
    ])
  ]),
  
  CANCELLED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('g', {}, [
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 18,
        y1: 6,
        x2: 6,
        y2: 18
      }),
      h('line', {
        stroke: getIconStroke(step),
        'stroke-width': 2,
        x1: 6,
        y1: 6,
        x2: 18,
        y2: 18
      })
    ])
  ]),
  
  ARCHIVED: (step: TimelineStep) => h('svg', { viewBox: '0 0 24 24', width: 20, height: 20 }, [
    h('path', {
      fill: getIconFill(step),
      stroke: getIconStroke(step),
      'stroke-width': 2,
      d: 'M3,3H21V7H3V3M4,8H20V21H4V8M9.5,11A0.5,0.5 0 0,0 9,11.5V13H15V11.5A0.5,0.5 0 0,0 14.5,11H9.5Z'
    })
  ])
}

// Get status icon
const getStatusIcon = (status: string, step: TimelineStep): VNode => {
  const icons = props.statusIcons || defaultStatusIcons
  const iconFn = icons[status] || defaultStatusIcons.NOT_STARTED
  return iconFn(step)
}

// Default action icons (filled versions for buttons)
const defaultActionIcons: StatusIconConfig = {
  OPEN: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('circle', { fill: 'currentColor', cx: 12, cy: 12, r: 8 })
  ]),
  
  NOT_STARTED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('circle', { fill: 'currentColor', cx: 12, cy: 12, r: 8 })
  ]),
  
  DRAFT: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6z'
    })
  ]),
  
  PLANNED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1'
    })
  ]),
  
  IN_PROGRESS: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M8,5.14V19.14L19,12.14L8,5.14Z' })
  ]),
  
  ACTIVE: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M8,5.14V19.14L19,12.14L8,5.14Z' })
  ]),
  
  ON_HOLD: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M14,19H18V5H14M6,19H10V5H6V19Z' })
  ]),
  
  PAUSED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', { fill: 'currentColor', d: 'M14,19H18V5H14M6,19H10V5H6V19Z' })
  ]),
  
  RESOLVED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z'
    })
  ]),
  
  COMPLETED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z'
    })
  ]),
  
  CLOSED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M12,2C6.47,2 2,6.47 2,12C2,17.53 6.47,22 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2M17,13H11V7H12.5V11.5H17V13Z'
    })
  ]),
  
  REJECTED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z'
    })
  ]),
  
  CANCELLED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z'
    })
  ]),
  
  ARCHIVED: () => h('svg', { viewBox: '0 0 24 24', width: 16, height: 16 }, [
    h('path', {
      fill: 'currentColor',
      d: 'M3,3H21V7H3V3M4,8H20V21H4V8M9.5,11A0.5,0.5 0 0,0 9,11.5V13H15V11.5A0.5,0.5 0 0,0 14.5,11H9.5Z'
    })
  ])
}

// Get action icon
const getActionIcon = (status: string): VNode => {
  const iconFn = defaultActionIcons[status] || defaultActionIcons.NOT_STARTED
  return iconFn({ status, date: null, isCurrent: false, isCompleted: false })
}

// Get icon fill color based on step
const getIconFill = (step: TimelineStep): string => {
  if (step.isCurrent || step.isCompleted) {
    return 'white'
  }
  return 'none'
}

// Get icon stroke color based on step
const getIconStroke = (step: TimelineStep): string => {
  if (step.isCurrent || step.isCompleted) {
    return 'white'
  }
  return 'currentColor'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.title), 1)
    ]),
    (_ctx.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('common.loading')) + "...", 1)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timelineSteps.value, (step, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `step-${index}`,
              class: _normalizeClass(["status-step", {
          current: step.isCurrent,
          completed: step.isCompleted
        }])
            }, [
              _createElementVNode("div", _hoisted_6, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(getStatusIcon(step.status, step))))
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, _toDisplayString(getStatusLabel(step.status)), 1),
                (step.date)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(step.date), 1))
                  : _createCommentVNode("", true)
              ])
            ], 2))
          }), 128))
        ])),
    (_ctx.updatePermission)
      ? (_openBlock(), _createBlock(PermissionGuard, {
          key: 2,
          permission: _ctx.updatePermission,
          "project-id": _ctx.projectId
        }, {
          default: _withCtx(() => [
            (_ctx.possibleStatuses.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.possibleStatuses, (status) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: status,
                      onClick: ($event: any) => (handleStatusChange(status)),
                      class: _normalizeClass(["status-action-btn", getStatusActionClass(status)]),
                      title: _ctx.$t(_ctx.transitionToKey, { status: getStatusLabel(status) })
                    }, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(getActionIcon(status)))),
                      _createElementVNode("span", null, _toDisplayString(getActionLabel(status)), 1)
                    ], 10, _hoisted_11))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["permission", "project-id"]))
      : _createCommentVNode("", true),
    _renderSlot(_ctx.$slots, "modal", {
      show: showModal.value,
      targetStatus: targetStatus.value,
      onSuccess: handleModalSuccess,
      onClose: closeModal
    })
  ]))
}
}

})